import { Link, useParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/common/Button';
import { Icon } from '../components/common/Icon';
import { Reveal } from '../components/animation/Reveal';
import { LoadingScreen } from '../components/common/Spinner';
import { ProductCard } from '../components/product/ProductCard';
import { breadcrumbSchema } from '../lib/seo';
import { useCategory, useCategories, useProductsByCategory } from '../hooks';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const { data: category, isLoading } = useCategory(slug);
  const { data: products = [] } = useProductsByCategory(slug);
  const { data: categories = [] } = useCategories();

  if (isLoading) {
    return (
      <div className="pt-[var(--nav-h)]">
        <LoadingScreen label="Loading category" />
      </div>
    );
  }

  if (!category) {
    return (
      <>
        <PageHeader title="Category not found" subtitle="This product category doesn’t exist." />
        <div className="container-x py-16">
          <Button to="/catalog" variant="primary" size="lg">
            <Icon name="ArrowLeft" className="h-5 w-5" /> All products
          </Button>
        </div>
      </>
    );
  }

  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/catalog' },
    { name: category.name, path: `/product-categories/${category.slug}` },
  ];

  return (
    <>
      <Seo
        title={`${category.name} — ${category.tagline} | GreenbayBio`}
        description={category.description}
        path={`/product-categories/${category.slug}`}
        jsonLd={[breadcrumbSchema(crumbs)]}
      />
      <PageHeader eyebrow={category.tagline} title={category.name} subtitle={category.description} accent={category.accentColor} />

      <div className="container-x py-14">
        <p className="mb-8 text-sm text-ink-500">
          <span className="font-semibold text-ink">{products.length}</span> {products.length === 1 ? 'product' : 'products'} in this category
        </p>
        {products.length > 0 ? (
          <Reveal className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" stagger={0.1} y={26}>
            {products.map((p, i) => (
              <ProductCard key={p.slug} product={p} priority={i < 3} />
            ))}
          </Reveal>
        ) : (
          <p className="rounded-xl border border-dashed border-sand-200 bg-white px-6 py-14 text-center text-ink-500">
            No products are assigned to this category yet.
          </p>
        )}
      </div>

      <div className="border-t border-sand-200 bg-sand-100/50">
        <div className="container-x py-12">
          <h2 className="mb-6 font-display text-h3 font-semibold text-ink">Other categories</h2>
          <div className="flex flex-wrap gap-3">
            {categories
              .filter((c) => c.slug !== category.slug)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/product-categories/${c.slug}`}
                  className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-700 transition-colors hover:border-brand-400 hover:text-brand-600"
                >
                  <span className="h-2 w-2 rounded-full" style={{ background: c.accentColor }} />
                  {c.name}
                </Link>
              ))}
          </div>
        </div>
      </div>
    </>
  );
}
